<template>
  <div class="elements-tab">
    <!-- Список элементов (типов) -->
    <div class="types-column">
      <div class="column-header">
        <div class="header-left">
          <q-checkbox
            v-if="selectedCategory && types.length"
            :model-value="allTypesSelected"
            @update:model-value="toggleAllTypes"
            size="xs"
            dense
            class="header-checkbox"
          />
          <span class="column-title">Элементы</span>
          <q-badge v-if="selectedCategory && types.length" :label="filteredTypes.length + '/' + types.length" color="grey-4" text-color="grey-8" />
        </div>
        <div v-if="selectedCategory" class="header-right">
          <q-btn
            v-if="selectedTypes.length > 0"
            icon="delete"
            flat
            dense
            round
            size="xs"
            color="negative"
            @click="confirmDeleteSelectedTypes"
          >
            <q-tooltip>Удалить выбранные ({{ selectedTypes.length }})</q-tooltip>
          </q-btn>
          <select
            v-model="typeFilterMode"
            @change="onLineFilterChange"
            class="filter-select"
          >
            <option value="all">Все</option>
            <option value="with_lines">С линиями</option>
            <option value="without_lines">Без линий</option>
          </select>
          <CustomButton
            class="import-btn"
            :loading="isImporting"
            @click="$refs.fileInput.click()"
          >
            <span>Импорт</span>
            <q-icon name="upload_file" class="import-btn__icon" size="14px" />
          </CustomButton>
          <CustomButton
            class="import-btn import-btn--excel"
            :loading="isImportingExcel"
            @click="$refs.excelFileInput.click()"
          >
            <span>Импорт Excel</span>
            <q-icon name="table_view" class="import-btn__icon" size="14px" />
          </CustomButton>
          <q-btn
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="openTypeDialog()"
          >
            <q-tooltip>Добавить элемент</q-tooltip>
          </q-btn>
        </div>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept=".json"
        style="display: none"
        @change="handleFileSelect"
      />
      <input
        ref="excelFileInput"
        type="file"
        accept=".xlsx,.xls,.csv"
        style="display: none"
        @change="handleExcelFileSelect"
      />
      <div v-if="selectedCategory && types.length" class="search-container">
        <q-input
          v-model="typeSearchQuery"
          dense
          outlined
          placeholder="Поиск элементов..."
          clearable
          class="search-input"
        >
          <template v-slot:prepend>
            <q-icon name="search" size="16px" />
          </template>
        </q-input>
      </div>
      <div v-if="!selectedCategory" class="empty-state">
        <q-icon name="arrow_back" color="grey-5" />
        <p>Выберите категорию слева</p>
      </div>
      <div v-else-if="isLoadingTypes" class="loading-container">
        <q-spinner color="primary" size="30px" />
      </div>
      <q-list v-else-if="filteredTypes.length > 0" dense class="column-list">
        <q-item
          v-for="type in filteredTypes"
          :key="type.bim_type_id"
          clickable
          v-ripple
          :active="selectedType?.bim_type_id === type.bim_type_id"
          @click="selectType(type)"
          class="list-item"
          dense
        >
          <q-item-section side style="min-width: 20px; padding-right: 4px;">
            <q-checkbox
              :model-value="selectedTypes.includes(type.bim_type_id)"
              @update:model-value="(val) => toggleTypeSelection(type.bim_type_id, val)"
              @click.stop
              size="xs"
              dense
            />
          </q-item-section>
          <q-item-section>
            <q-item-label class="item-label">{{ type.type_name }}</q-item-label>
            <q-item-label caption class="item-caption">ID: {{ type.bim_type_id }}</q-item-label>
          </q-item-section>
          <q-item-section side class="lines-indicator">
            <span v-if="hasLines(type)" class="lines-indicator__wrap">
              <q-icon name="check_circle" size="18px" color="positive" />
              <span class="lines-count">{{ linesCountLabel(type) }}</span>
              <q-tooltip>Заполнено линий: {{ countLines(type) }}</q-tooltip>
            </span>
            <span v-else class="lines-indicator__wrap">
              <q-icon name="warning" size="18px" color="grey-6" />
              <q-tooltip>Нет линий</q-tooltip>
            </span>
          </q-item-section>
          <q-item-section side>
            <q-btn
              icon="edit"
              flat
              dense
              round
              size="xs"
              color="primary"
              @click.stop="openTypeDialog(type)"
            >
              <q-tooltip>Редактировать</q-tooltip>
            </q-btn>
          </q-item-section>
        </q-item>
      </q-list>
      <div v-else class="empty-state">
        <q-icon name="search_off" color="grey-5" />
        <p>{{ typeSearchQuery ? 'Ничего не найдено' : 'Нет элементов' }}</p>
      </div>
    </div>

    <!-- Правая колонка: линии выбранного элемента -->
    <div class="lines-column">
      <div v-if="!selectedType" class="empty-state">
        <q-icon name="arrow_back" color="grey-5" />
        <p>← Выберите элемент</p>
      </div>
      <template v-else>
        <div class="column-header">
          <span class="column-title">Линии</span>
        </div>
        <div class="lines-form">
          <div
            v-for="(lineKey, idx) in visibleLineKeys"
            :key="lineKey"
            class="line-row"
          >
            <label class="line-label">Линия {{ lineNumberByKey(lineKey) }}</label>
            <CustomSelect
              :value="lineValue(lineKey)"
              @change="(val) => setLineValue(lineKey, val)"
              :options="floorParamTypes"
              option-label-key="floor_param_type_name"
              option-value-key="floor_param_type_id"
              placeholder="Выберите линию"
              :loading="isLoadingFloorParamTypes"
              :clearable="true"
              dense
            />
          </div>
        </div>
        <div class="lines-actions">
          <CustomButton
            size="sm"
            color="primary-light"
            @click="saveLines"
            :loading="isSavingLines"
          >
            Сохранить линии
          </CustomButton>
        </div>
      </template>
    </div>

    <!-- Модалка: добавление/редактирование элемента (название + линии) -->
    <q-dialog v-model="typeDialog" persistent>
      <q-card class="bim-modal">
        <q-card-section class="bim-modal__header">
          <div class="bim-modal__title">{{ editingType ? 'Редактировать элемент' : 'Добавить элемент' }}</div>
          <q-btn icon="close" flat round dense v-close-popup class="bim-modal__close" />
        </q-card-section>

        <q-card-section class="bim-modal__content">
          <q-form @submit="saveType" class="bim-form">
            <div class="bim-form__section">
              <div class="bim-form__item">
                <CustomInput
                  label="Название"
                  type="text"
                  :required="true"
                  :value="typeForm.type_name"
                  @change_input="(value) => typeForm.type_name = value"
                  autofocus
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Линия 1"
                  :options="floorParamTypes"
                  option-label-key="floor_param_type_name"
                  option-value-key="floor_param_type_id"
                  :value="typeForm.floor_param_type_id"
                  @change="(value) => typeForm.floor_param_type_id = value"
                  :loading="isLoadingFloorParamTypes"
                  placeholder="Выберите линию"
                  :clearable="true"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Линия 2"
                  :options="floorParamTypes"
                  option-label-key="floor_param_type_name"
                  option-value-key="floor_param_type_id"
                  :value="typeForm.floor_param_type_id2"
                  @change="(value) => typeForm.floor_param_type_id2 = value"
                  :loading="isLoadingFloorParamTypes"
                  placeholder="Выберите линию 2"
                  :clearable="true"
                />
              </div>
              <div class="bim-form__item">
                <CustomSelect
                  label="Линия 3"
                  :options="floorParamTypes"
                  option-label-key="floor_param_type_name"
                  option-value-key="floor_param_type_id"
                  :value="typeForm.floor_param_type_id3"
                  @change="(value) => typeForm.floor_param_type_id3 = value"
                  :loading="isLoadingFloorParamTypes"
                  placeholder="Выберите линию 3"
                  :clearable="true"
                />
              </div>
            </div>

            <div class="bim-modal__footer">
              <CustomButton
                @click="typeDialog = false"
                :disabled="isSaving"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton
                type="submit"
                :loading="isSaving"
              >
                {{ editingType ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Подтверждение удаления -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>
        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Прогресс удаления -->
    <q-dialog v-model="deletionProgressDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Удаление элементов</div>
        </q-card-section>
        <q-card-section>
          <div style="margin-bottom: 10px;">
            Удалено: {{ deletionProgress.current }} из {{ deletionProgress.total }}
          </div>
          <q-linear-progress
            :value="deletionProgress.current / deletionProgress.total"
            color="primary"
            size="20px"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge color="white" text-color="primary" :label="`${Math.round((deletionProgress.current / (deletionProgress.total || 1)) * 100)}%`" />
            </div>
          </q-linear-progress>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import {
  getBimTypes,
  getFloorParamTypes,
  saveBimType,
  deleteBimType,
  importBimJson,
  importBimExcelDict
} from "@/views/BimCategories/services";

export default {
  name: "ElementsTab",
  components: {
    CustomButton,
    CustomInput,
    CustomSelect
  },
  props: {
    selectedCategory: { type: Object, default: null },
    selectedCategoryId: { type: Number, default: null }
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
  data() {
    return {
      types: [],
      selectedType: null,
      isLoadingTypes: false,
      isLoadingFloorParamTypes: false,
      floorParamTypes: [],
      typeSearchQuery: '',
      typeDialog: false,
      editingType: null,
      isSaving: false,
      isSavingLines: false,
      typeForm: {
        type_name: '',
        floor_param_type_id: null,
        floor_param_type_id2: null,
        floor_param_type_id3: null
      },
      lineKeys: ['floor_param_type_id', 'floor_param_type_id2', 'floor_param_type_id3'],
      linesDraft: {},
      isImporting: false,
      isImportingExcel: false,
      typeFilterMode: localStorage.getItem('bim_elements_line_filter_mode') || 'all',
      selectedTypes: [],
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      deletionProgressDialog: false,
      deletionProgress: { current: 0, total: 0 }
    };
  },
  emits: ['import-done'],
  computed: {
    allTypesSelected() {
      return this.filteredTypes.length > 0 && this.selectedTypes.length === this.filteredTypes.length;
    },
    filteredTypes() {
      let list = this.types || [];
      if (this.typeFilterMode === 'with_lines') {
        list = list.filter(t => this.hasLines(t));
      } else if (this.typeFilterMode === 'without_lines') {
        list = list.filter(t => !this.hasLines(t));
      }
      if (this.typeSearchQuery) {
        const q = this.typeSearchQuery.toLowerCase();
        list = list.filter(t =>
          (t.type_name && t.type_name.toLowerCase().includes(q)) ||
          String(t.bim_type_id).includes(q)
        );
      }
      return list;
    },
    visibleLineKeys() {
      const keys = this.lineKeys;
      const filled = keys.filter(k => this.lineValue(k));
      if (filled.length >= 3) return filled;
      const nextEmpty = keys.find(k => !this.lineValue(k));
      return nextEmpty ? [...filled, nextEmpty] : keys;
    }
  },
  watch: {
    selectedType: {
      handler(type) {
        this.linesDraft = {};
        if (type) {
          this.linesDraft = {
            floor_param_type_id: type.floor_param_type_id || null,
            floor_param_type_id2: type.floor_param_type_id2 || null,
            floor_param_type_id3: type.floor_param_type_id3 || null
          };
        }
      },
      immediate: true
    },
    selectedCategoryId: {
      handler(id) {
        this.selectedType = null;
        this.selectedTypes = [];
        this.types = [];
        if (id) this.loadTypes(id);
      },
      immediate: true
    }
  },
  mounted() {
    this.loadFloorParamTypes();
  },
  methods: {
    async loadTypes(bim_category_id) {
      this.isLoadingTypes = true;
      try {
        const res = await getBimTypes(bim_category_id);
        if (res) this.types = res;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingTypes = false;
      }
    },
    async loadFloorParamTypes() {
      this.isLoadingFloorParamTypes = true;
      try {
        const res = await getFloorParamTypes();
        if (res) this.floorParamTypes = res;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingFloorParamTypes = false;
      }
    },
    hasLines(type) {
      if (!type) return false;
      return !!(type.floor_param_type_id || type.floor_param_type_id2 || type.floor_param_type_id3);
    },
    countLines(type) {
      if (!type) return 0;
      let n = 0;
      if (type.floor_param_type_id) n++;
      if (type.floor_param_type_id2) n++;
      if (type.floor_param_type_id3) n++;
      return n;
    },
    linesCountLabel(type) {
      const n = this.countLines(type);
      if (n === 0) return '';
      if (n === 1) return '1 линия';
      if (n >= 2 && n <= 4) return n + ' линии';
      return n + ' линий';
    },
    onLineFilterChange() {
      localStorage.setItem('bim_elements_line_filter_mode', this.typeFilterMode);
    },
    toggleAllTypes(selected) {
      if (selected) {
        const filteredIds = this.filteredTypes.map(t => t.bim_type_id);
        this.selectedTypes = [...new Set([...this.selectedTypes, ...filteredIds])];
      } else {
        const filteredIds = this.filteredTypes.map(t => t.bim_type_id);
        this.selectedTypes = this.selectedTypes.filter(id => !filteredIds.includes(id));
      }
    },
    toggleTypeSelection(typeId, selected) {
      if (selected) {
        if (!this.selectedTypes.includes(typeId)) this.selectedTypes.push(typeId);
      } else {
        const idx = this.selectedTypes.indexOf(typeId);
        if (idx >= 0) this.selectedTypes.splice(idx, 1);
      }
    },
    confirmDeleteSelectedTypes() {
      this.confirmDialog = {
        show: true,
        title: 'Удаление элементов',
        message: `Вы уверены, что хотите удалить ${this.selectedTypes.length} элемент(ов)?`,
        onConfirm: () => this.deleteSelectedTypes()
      };
    },
    onConfirmDelete() {
      if (this.confirmDialog.onConfirm) this.confirmDialog.onConfirm();
      this.confirmDialog.show = false;
    },
    async deleteSelectedTypes() {
      this.deletionProgress = { current: 0, total: this.selectedTypes.length };
      this.deletionProgressDialog = true;
      const typesToDelete = [...this.selectedTypes];
      for (const typeId of typesToDelete) {
        try {
          await deleteBimType(typeId);
        } catch (e) {
          console.error(e);
          this.showErrorSnackbar(e?.response?.data?.message || `Ошибка удаления элемента ${typeId}`);
        }
        this.deletionProgress.current++;
      }
      this.deletionProgressDialog = false;
      this.selectedTypes = [];
      this.showSucceedSnackbar(`Удалено элементов: ${typesToDelete.length}`);
      if (this.selectedType && typesToDelete.includes(this.selectedType.bim_type_id)) {
        this.selectedType = null;
      }
      if (this.selectedCategoryId) {
        const res = await getBimTypes(this.selectedCategoryId);
        if (res) this.types = res;
      }
    },
    selectType(type) {
      this.selectedType = type;
    },
    async handleFileSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.isImporting = true;
      try {
        const res = await importBimJson(file);
        if (res) {
          this.showSucceedSnackbar('JSON успешно импортирован');
          this.$emit('import-done');
          if (this.selectedCategoryId) {
            await this.loadTypes(this.selectedCategoryId);
          }
        }
      } catch (e) {
        console.error(e);
        this.showErrorSnackbar(
          e?.response?.data?.message || e?.response?.data?.error || 'Ошибка импорта',
        );
      } finally {
        this.isImporting = false;
      }
      event.target.value = '';
    },
    async handleExcelFileSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.isImportingExcel = true;
      try {
        const res = await importBimExcelDict(file);
        if (res) {
          this.showSucceedSnackbar(res.message || 'Excel успешно импортирован');
          this.$emit('import-done');
          if (this.selectedCategoryId) {
            await this.loadTypes(this.selectedCategoryId);
          }
        }
      } catch (e) {
        console.error(e);
        this.showErrorSnackbar(
          e?.response?.data?.message || e?.response?.data?.error || 'Ошибка импорта Excel',
        );
      } finally {
        this.isImportingExcel = false;
      }
      event.target.value = '';
    },
    lineValue(key) {
      if (this.linesDraft[key] !== undefined) return this.linesDraft[key];
      return this.selectedType ? (this.selectedType[key] || null) : null;
    },
    setLineValue(key, val) {
      this.linesDraft = { ...this.linesDraft, [key]: val };
    },
    lineNumberByKey(key) {
      const idx = this.lineKeys.indexOf(key);
      return idx >= 0 ? idx + 1 : 0;
    },
    async saveLines() {
      if (!this.selectedType) return;
      this.isSavingLines = true;
      try {
        await saveBimType({
          bim_type_id: this.selectedType.bim_type_id,
          bim_category_id: this.selectedCategory.bim_category_id,
          type_name: this.selectedType.type_name,
          floor_param_type_id: this.linesDraft.floor_param_type_id || null,
          floor_param_type_id2: this.linesDraft.floor_param_type_id2 || null,
          floor_param_type_id3: this.linesDraft.floor_param_type_id3 || null
        });
        this.showSucceedSnackbar('Линии сохранены');
        const res = await getBimTypes(this.selectedCategory.bim_category_id);
        if (res) this.types = res;
        const updated = res?.find(t => t.bim_type_id === this.selectedType.bim_type_id);
        if (updated) this.selectedType = updated;
      } catch (e) {
        console.error(e);
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка сохранения линий');
      } finally {
        this.isSavingLines = false;
      }
    },
    openTypeDialog(type = null) {
      this.editingType = type;
      if (type) {
        this.typeForm = {
          type_name: type.type_name,
          floor_param_type_id: type.floor_param_type_id || null,
          floor_param_type_id2: type.floor_param_type_id2 || null,
          floor_param_type_id3: type.floor_param_type_id3 || null
        };
      } else {
        this.typeForm = {
          type_name: '',
          floor_param_type_id: null,
          floor_param_type_id2: null,
          floor_param_type_id3: null
        };
      }
      this.typeDialog = true;
    },
    async saveType() {
      if (!this.typeForm.type_name) {
        this.showErrorSnackbar('Введите название');
        return;
      }
      this.isSaving = true;
      try {
        await saveBimType({
          bim_type_id: this.editingType?.bim_type_id || null,
          bim_category_id: this.selectedCategory.bim_category_id,
          type_name: this.typeForm.type_name,
          floor_param_type_id: this.typeForm.floor_param_type_id || null,
          floor_param_type_id2: this.typeForm.floor_param_type_id2 || null,
          floor_param_type_id3: this.typeForm.floor_param_type_id3 || null
        });
        this.showSucceedSnackbar(this.editingType ? 'Элемент обновлен' : 'Элемент добавлен');
        this.typeDialog = false;
        const res = await getBimTypes(this.selectedCategory.bim_category_id);
        if (res) this.types = res;
      } catch (e) {
        console.error(e);
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка сохранения');
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.elements-tab {
  display: flex;
  height: 100%;
  gap: 20px;
  min-height: 400px;
}

.types-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fafafa;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  flex-wrap: wrap;
}

.column-header .header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.column-header .header-checkbox {
  flex-shrink: 0;
}

.column-header .header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.column-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.filter-select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  color: #333;
  cursor: pointer;
  min-width: 120px;
}

.filter-select:hover {
  border-color: #1976d2;
}

.filter-select:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.lines-indicator {
  min-width: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lines-indicator__wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: default;
}

.lines-count {
  font-size: 11px;
  color: #2e7d32;
  font-weight: 500;
  white-space: nowrap;
}


.import-btn {
  width: auto;
  height: 32px;
  font-size: 12px;
  min-width: auto;
  padding: 0 12px;
}

.import-btn__icon {
  margin-left: 4px;
}

.import-btn :deep(.q-btn__content) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.column-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.list-item {
  border-radius: 8px;
  margin-bottom: 4px;
}

.list-item:hover {
  background: #f5f5f5;
}

.search-container {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  width: 100%;
}

.item-label {
  font-weight: 500;
}

.item-caption {
  font-size: 12px;
  color: #666;
}

.lines-column {
  flex-shrink: 0;
  width: 280px;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.lines-column .column-header {
  padding: 16px;
}

.lines-form {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.line-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.line-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.lines-actions {
  padding: 0 16px 16px;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
  text-align: center;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.loading-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.bim-modal {
  min-width: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.bim-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.bim-modal__title {
  font-size: 18px;
  font-weight: 600;
}

.bim-modal__close {
  margin: -8px;
}

.bim-modal__content {
  padding: 24px 28px;
}

.bim-form__section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bim-modal__footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  margin-top: 16px;
}
</style>
